import {
  Plus,
  Minus,
  Trash2,
  X,
  Check,
  CreditCard,
  Banknote,
  DollarSign,
  Truck,
  User,
  Users,
  AlertCircle,
  Key,
  LogOut,
  Printer,
  FileText,
  Calendar,
  Monitor,
  CheckSquare,
  LogIn,
  Phone,
  MapPin,
  Loader2,
  Search,
  Zap,
} from 'lucide-react'
import { toast } from 'sonner'
import React, { useState, useEffect, useRef } from 'react'

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error: Error | null }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 text-red-600 bg-red-50 rounded-lg m-4 border border-red-200">
          <h1 className="text-xl font-bold mb-4">¡Ocurrió un error en la interfaz!</h1>
          <p className="mb-2">Por favor toma captura de este error para que el equipo de desarrollo lo revise:</p>
          <pre className="p-4 bg-red-100 rounded text-sm overflow-auto max-w-full">
            {this.state.error?.message}
            <br /><br />
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }
    return this.props.children;
  }
}
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useHotkeys } from 'react-hotkeys-hook'
import { ProductCard } from '../components/pos/ProductCard'
import { ProductDiscoveryControls } from '../components/pos/ProductDiscoveryControls'
import { ShortcutsWidget } from '../components/pos/ShortcutsWidget'

import { PosCart } from '../components/pos/PosCart'


import { CashCountModal, CashCountResultModal } from '../components/pos/modals/CashCountModal'
import { CashDepositModal } from '../components/pos/modals/CashDepositModal'
import { CashWithdrawalModal } from '../components/pos/modals/CashWithdrawalModal'
import { ChangeCashierModal } from '../components/pos/modals/ChangeCashierModal'
import { DeliveryDetailsModal } from '../components/pos/modals/DeliveryDetailsModal'
import { NewCustomerModal } from '../components/pos/modals/NewCustomerModal'
import { PaymentModal, SuccessModal } from '../components/pos/modals/PaymentModal'
import { PrescriptionModal } from '../components/pos/modals/PrescriptionModal'
import { TicketPreviewModal } from '../components/pos/modals/TicketPreviewModal'
import { RegisterAssignmentModal } from '../components/pos/RegisterAssignmentModal'
import { OpenRegisterSessionModal } from '../components/pos/modals/OpenRegisterSessionModal'
import { InvoiceDataModal, InvoiceData } from '../components/pos/modals/InvoiceDataModal'
import { PriceCheckModal } from '../components/pos/modals/PriceCheckModal'
import { WbsRechargeModal } from '../components/pos/modals/WbsRechargeModal'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { Checkbox } from '../components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select'
import { Textarea } from '../components/ui/textarea'

import {
  Product,
  CartItem,
  DeliveryDetails,
  POINT_VALUE,

  posCustomerService,
  posProductService,
  PosUseCase,
  posRegisterService,
  posCartService,
  posSessionService,
  posCashMovementService,
  posSalesService,
  PosCartData,
  cartFromValidationError,
  posCartExpirationDelay,
  posCartRefetchInterval,
} from '../services/pos.service'
import { facturamaService } from '../services/facturama.service'
import { paymentMethodService, type PaymentMethodItem } from '../services/payment-method.service'


export function PointOfSale() {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedUseCase, setSelectedUseCase] = useState('all')
  const [productPage, setProductPage] = useState(1)
  const [lastProductPage, setLastProductPage] = useState(1)
  const [customers, setCustomers] = useState<any[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [searchTerm, setSearchTerm] = useState('')
  const autoAddInFlightRef = useRef<string | null>(null)
  const lastAutoAddedSearchRef = useRef<string | null>(null)
  const reloadCustomers = async () => {
    try {
      const customersData = await posCustomerService.getCustomers();
      setCustomers(customersData);
    } catch (error) {
      console.error("Error recargando clientes", error);
    }
  };

  useEffect(() => {
    const loadCatalogs = async () => {
      try {
        const [customersData] = await Promise.all([
          posCustomerService.getCustomers(),
        ]);
        setCustomers(customersData);
      } catch (error) {
        console.error("Error cargando datos", error);
      }
    };
    void loadCatalogs();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      lastAutoAddedSearchRef.current = null
    }
  }, [searchTerm])

  const {
    data: registerContext,
    isLoading: isLoadingContext,
    refetch: refetchContext,
  } = useQuery({
    queryKey: ['register-context'],
    queryFn: posRegisterService.getRegisterContext,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  })

  const { data: facturamaProfile } = useQuery({
    queryKey: ['facturama-profile'],
    queryFn: facturamaService.getProfile,
    staleTime: 1000 * 60 * 60 * 2, // 2 horas
  })

  const { data: paymentMethods = [] } = useQuery<PaymentMethodItem[]>({
    queryKey: ['payment-methods', 'pos'],
    queryFn: async () => {
      const response = await paymentMethodService.getItems({ all: true })
      const items = response.data?.data || response.data || []

      return Array.isArray(items) ? items : []
    },
    staleTime: 1000 * 60 * 5,
  })

  // Modales de contexto
  const [showRegisterAssignment, setShowRegisterAssignment] = useState(false)
  const [showOpenSession, setShowOpenSession] = useState(false)
  const [showPriceCheckModal, setShowPriceCheckModal] = useState(false)
  const [showWbsModal, setShowWbsModal] = useState(false)
  const [productRefreshTrigger, setProductRefreshTrigger] = useState(0)
  const queryClient = useQueryClient()

  useEffect(() => {
    const controller = new AbortController()
    const normalizedSearch = searchTerm.trim()
    const searchKey = normalizedSearch.toLowerCase()
    setIsLoadingData(true)

    posProductService.getProducts({
      search: normalizedSearch || undefined,
      use_case_id: selectedUseCase === 'all' ? undefined : Number(selectedUseCase),
      page: productPage,
      per_page: 20,
    }, controller.signal).then((result) => {
      setProducts((current) => productPage === 1 ? result.data : [...current, ...result.data])
      setLastProductPage(result.last_page || 1)

      const shouldAutoAdd =
        productPage === 1
        && selectedUseCase === 'all'
        && normalizedSearch !== ''
        && Number(result.total) === 1
        && result.data.length === 1
        && autoAddInFlightRef.current !== searchKey
        && lastAutoAddedSearchRef.current !== searchKey

      if (!shouldAutoAdd) return

      const product = result.data[0]
      autoAddInFlightRef.current = searchKey

      posCartService.createCartItem({ product_id: product.id, quantity: 1 })
        .then((nextCart) => {
          queryClient.setQueryData(['pos-cart'], nextCart)
          lastAutoAddedSearchRef.current = searchKey
          setSearchTerm('')
          setProducts([])
          setProductPage(1)
          window.setTimeout(() => document.getElementById('product-search-input')?.focus(), 0)
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message || 'Error al agregar producto')
        })
        .finally(() => {
          if (autoAddInFlightRef.current === searchKey) {
            autoAddInFlightRef.current = null
          }
        })
    }).catch((error) => {
      if (error?.code !== 'ERR_CANCELED') console.error('Error cargando productos', error)
    }).finally(() => {
      if (!controller.signal.aborted) setIsLoadingData(false)
    })

    return () => controller.abort()
  }, [searchTerm, selectedUseCase, productPage, productRefreshTrigger, queryClient])

  const { data: cartData, isLoading: isLoadingCart } = useQuery({
    queryKey: ['pos-cart'],
    queryFn: posCartService.getCart,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: (query) => {
      const currentCart = query.state.data as PosCartData | undefined

      return posCartRefetchInterval(currentCart)
    },
    refetchIntervalInBackground: false,
  })

  const [selectedItemIds, setSelectedItemIds] = useState<number[]>([])
  const cart = (cartData?.items || []).map((item: CartItem) => ({
    ...item,
    selected: selectedItemIds.includes(item.id)
  }))
  const syncCart = (nextCart: PosCartData) => {
    queryClient.setQueryData(['pos-cart'], nextCart)
  }

  useEffect(() => {
    const expirationDelay = posCartExpirationDelay(cartData)

    if (expirationDelay === null) return

    const timeout = window.setTimeout(
      () => queryClient.invalidateQueries({ queryKey: ['pos-cart'] }),
      expirationDelay,
    )

    return () => window.clearTimeout(timeout)
  }, [cartData, queryClient])

  const addItemMutation = useMutation({
    mutationFn: posCartService.createCartItem,
    onSuccess: syncCart,
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al agregar producto')
  })

  const updateItemMutation = useMutation({
    mutationFn: ({ id, quantity }: { id: number; quantity: number }) => posCartService.updateCartItem(id, { quantity }),
    onSuccess: syncCart,
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al actualizar cantidad')
  })

  const removeItemMutation = useMutation({
    mutationFn: posCartService.deleteCartItem,
    onSuccess: syncCart,
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al eliminar producto')
  })

  const closeSessionMutation = useMutation({
    mutationFn: posSessionService.updateSession,
    onSuccess: (data) => {
      setClosedSessionData(data)
      toast.success('Sesión de caja cerrada correctamente.')
      queryClient.invalidateQueries({ queryKey: ['register-context'] })

      // Compute status for the result modal
      const counted = parseFloat(countedCash || '0')
      const difference = counted - currentCashInRegister
      setCashCountDifference(difference)

      if (difference === 0) {
        setCashCountStatus('success')
      } else if (difference > 0) {
        setCashCountStatus('surplus')
      } else {
        setCashCountStatus('shortage')
      }

      setShowCashCount(false)
      setShowCashCountResult(true)
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || 'Error al cerrar la caja'
      toast.error(msg)
    }
  })

  const depositMutation = useMutation({
    mutationFn: posCashMovementService.createDeposit,
    onSuccess: () => {
      toast.success('Depósito registrado correctamente.')
      refetchContext()
      setDepositAmount('')
      setDepositConcept('')
      setShowCashDeposit(false)
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || 'Error al registrar depósito'
      toast.error(msg)
    }
  })

  const withdrawalMutation = useMutation({
    mutationFn: posCashMovementService.createWithdrawal,
    onSuccess: () => {
      toast.success('Retiro registrado correctamente.')
      refetchContext()
      setWithdrawalAmount('')
      setWithdrawalConcept('')
      setShowCashWithdrawal(false)
    },
    onError: (error: any) => {
      const msg = error?.response?.data?.message || 'Error al registrar retiro'
      toast.error(msg)
    }
  })

  const clearCartMutation = useMutation({
    mutationFn: posCartService.clearCart,
    onSuccess: syncCart,
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al vaciar el carrito')
  })

  const releaseMutation = useMutation({
    mutationFn: (password: string) => {
      const registerId = registerContext?.cash_register?.id
      if (!registerId) throw new Error('No hay una caja asignada.')
      return posRegisterService.deleteRegisterAssignment(registerId, password)
    },
    onSuccess: () => {
      toast.success('Caja liberada correctamente')
      refetchContext()
      setReleasePassword('')
      setShowChangeCashier(false)
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al liberar la caja')
  })

  const validateCartMutation = useMutation({
    mutationFn: posCartService.validateCart,
    onSuccess: syncCart,
    onError: (err: any) => {
      const recalculatedCart = cartFromValidationError(err)

      if (recalculatedCart) syncCart(recalculatedCart)
      toast.error(err?.response?.data?.message || 'Error al validar el carrito')
    },
  })

  const processSaleMutation = useMutation({
    mutationFn: posSalesService.createSale,
    onSuccess: (data) => {
      // Data contains the processed sale and ticket information
      queryClient.invalidateQueries({ queryKey: ['pos-cart'] })
      queryClient.invalidateQueries({ queryKey: ['pos-cash-movements'] })
      queryClient.invalidateQueries({ queryKey: ['sales'] })
      refetchContext()
      setProductRefreshTrigger(Date.now())
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || 'Error al procesar la venta')
  })
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showTicketPreview, setShowTicketPreview] = useState(false)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [completedSaleTotal, setCompletedSaleTotal] = useState(0)
  const [showCashWithdrawal, setShowCashWithdrawal] = useState(false)
  const [showCashCount, setShowCashCount] = useState(false)
  const [showCashCountResult, setShowCashCountResult] = useState(false)
  const [showChangeCashier, setShowChangeCashier] = useState(false)
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false)
  const [showNewCustomerModal, setShowNewCustomerModal] = useState(false)
  const [showDeliveryDetailsModal, setShowDeliveryDetailsModal] =
    useState(false)
  const [showCashDeposit, setShowCashDeposit] = useState(false)
  const [showInvoiceDataModal, setShowInvoiceDataModal] = useState(false)
  const [closedSessionData, setClosedSessionData] = useState<any>(null)

  // Invoice status
  const [invoiceStatus, setInvoiceStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [invoiceError, setInvoiceError] = useState<string | null>(null)
  const [cfdiPdfBase64, setCfdiPdfBase64] = useState<string | null>(null)
  const [invoiceData, setInvoiceData] = useState<InvoiceData>({
    rfc: '',
    name: '',
    cfdi_use: '',
    fiscal_regime: '',
    zip_code: '',
    email: '',
  })

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    string | null
  >(null)
  const [cashReceived, setCashReceived] = useState('')
  const [currentCashInRegister, setCurrentCashInRegister] = useState(2500)
  const [selectedBank, setSelectedBank] = useState<any>(null)
  const [usePoints, setUsePoints] = useState(false)
  const [reference, setReference] = useState('')
  const [pointsToUse, setPointsToUse] = useState(0)
  const [releasePassword, setReleasePassword] = useState('')

  // Sale type
  const [saleType, setSaleType] = useState<'regular' | 'delivery'>('regular')
  const [selectedDeliveryPerson, setSelectedDeliveryPerson] = useState('')
  const [deliveryNotes, setDeliveryNotes] = useState('')

  // Customer
  const [customerType, setCustomerType] = useState<'general' | 'registered'>(
    'general',
  )
  const [selectedCustomer, setSelectedCustomer] = useState<number | null>(null)

  // Invoice
  const [requiresInvoice, setRequiresInvoice] = useState(false)
  const [ticketCopies, setTicketCopies] = useState(1)



  // Cash count
  const [countedCash, setCountedCash] = useState('')
  const [denominations, setDenominations] = useState({
    1: 0,
    2: 0,
    5: 0,
    10: 0,
    20: 0,
    50: 0,
    100: 0,
    200: 0,
    500: 0,
    1000: 0,
  })
  const [withdrawalAmount, setWithdrawalAmount] = useState('')
  const [withdrawalConcept, setWithdrawalConcept] = useState('')
  const [depositAmount, setDepositAmount] = useState('')
  const [depositConcept, setDepositConcept] = useState('')
  const [cashCountDifference, setCashCountDifference] = useState(0)
  const [cashCountNotes, setCashCountNotes] = useState('')
  const [cashCountStatus, setCashCountStatus] = useState<
    'success' | 'surplus' | 'shortage'
  >('success')

  useEffect(() => {
    if (showCashCount) {
      setCountedCash('')
      setCashCountNotes('')
      setDenominations({
        1: 0, 2: 0, 5: 0, 10: 0, 20: 0, 50: 0, 100: 0, 200: 0, 500: 0, 1000: 0,
      })
    }
  }, [showCashCount])
  // Delivery details
  const [deliveryDetails, setDeliveryDetails] = useState<DeliveryDetails>({
    deliveryUserId: null,
    recipientName: '',
    customerPhone: '',
    customerAddress: '',
    observations: '',
    deliveryCost: 0,
  })

  // Ticket info
  const [ticketNumber, setTicketNumber] = useState('')
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false)
  useEffect(() => {
    const handleAltPress = (e: KeyboardEvent) => {
      if (e.key === 'Alt') {
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', handleAltPress)
    window.addEventListener('keyup', handleAltPress)
    return () => {
      window.removeEventListener('keydown', handleAltPress)
      window.removeEventListener('keyup', handleAltPress)
    }
  }, [])

  // accesibilidad de teclado
  useHotkeys(['f10', 'alt+h'], (e) => {
    e.preventDefault()
    setShowShortcutsHelp(prev => !prev)
  }, { enableOnFormTags: true }, [])

  useHotkeys(['f2', 'alt+b'], (e) => {
    e.preventDefault()
    document.getElementById('product-search-input')?.focus()
  }, { enableOnFormTags: true }, [])

  useHotkeys(['f8', 'alt+p'], (e) => {
    e.preventDefault()
    if (cart.length > 0) document.getElementById('process-payment-button')?.click()
  }, { enableOnFormTags: true }, [cart])

  useHotkeys('esc', (e) => {
    if (!showPaymentModal && !showTicketPreview && !showCashWithdrawal && !showCashDeposit && !showCashCount && !showChangeCashier && !showDeliveryDetailsModal) {
      setSearchTerm('')
    }
  }, { enableOnFormTags: true }, [showPaymentModal, showTicketPreview, showCashWithdrawal, showCashDeposit, showCashCount, showChangeCashier, showDeliveryDetailsModal])

  useHotkeys('alt+s', (e) => {
    e.preventDefault()
    if (saleType === 'delivery') {
      setSaleType('regular')
    } else {
      setSaleType('delivery')
      setShowDeliveryDetailsModal(true)
    }
  }, { enableOnFormTags: true }, [saleType])

  useHotkeys('alt+l', (e) => {
    e.preventDefault()
    if (cart.length > 0) clearCartMutation.mutate()
  }, { enableOnFormTags: true }, [cart])

  useHotkeys('alt+up', (e) => {
    e.preventDefault()
    if (cart.length > 0) {
      const lastItem = cart[cart.length - 1]
      updateQuantity(lastItem.id, 1)
    }
  }, { enableOnFormTags: true }, [cart])

  useHotkeys('alt+down', (e) => {
    e.preventDefault()
    if (cart.length > 0) {
      const lastItem = cart[cart.length - 1]
      updateQuantity(lastItem.id, -1)
    }
  }, { enableOnFormTags: true }, [cart])

  useHotkeys(['alt+delete', 'alt+backspace'], (e) => {
    e.preventDefault()
    if (cart.length > 0) {
      const lastItem = cart[cart.length - 1]
      removeFromCart(lastItem.id)
    }
  }, { enableOnFormTags: true }, [cart])

  useHotkeys('alt+r', (e) => {
    e.preventDefault()
    setShowCashWithdrawal(true)
  }, { enableOnFormTags: true }, [])

  useHotkeys('alt+d', (e) => {
    e.preventDefault()
    setShowCashDeposit(true)
  }, { enableOnFormTags: true }, [])

  useHotkeys('alt+c', (e) => {
    e.preventDefault()
    setShowCashCount(true)
  }, { enableOnFormTags: true }, [])

  useHotkeys('f4', (e) => {
    e.preventDefault()
    setShowPriceCheckModal(true)
  }, { enableOnFormTags: true }, [])

  useHotkeys('alt+w', (e) => {
    e.preventDefault()
    setShowWbsModal(true)
  }, { enableOnFormTags: true }, [])

  useHotkeys('alt+x', (e) => {
    e.preventDefault()
    if (registerContext?.cash_register?.id) {
      setShowChangeCashier(true)
    }
  }, { enableOnFormTags: true }, [registerContext])


  useEffect(() => {
    if (!isLoadingContext && registerContext) {
      if (!registerContext.cash_register || !registerContext.cash_register.is_active) {
        setShowRegisterAssignment(true)
        setShowOpenSession(false)
      } else if (!registerContext.session) {
        if (!showCashCountResult && !showChangeCashier) {
          setShowRegisterAssignment(false)
          setShowOpenSession(true)
        }
      } else {
        setShowRegisterAssignment(false)
        setShowOpenSession(false)
        setCurrentCashInRegister(
          parseFloat(registerContext.session.cash_balance_amount) ||
          parseFloat(registerContext.session.opening_cash_amount) ||
          0
        )
      }
    }
  }, [isLoadingContext, registerContext, showCashCountResult, showChangeCashier])

  const addToCart = (product: Product) => {
    addProductToCart(product)
  }

  const addProductToCart = (product: Product) => {
    addItemMutation.mutate({ product_id: product.id, quantity: 1 })
  }

  const updateQuantity = (id: number, delta: number) => {
    const item = cart.find((i: any) => i.id === id)
    if (!item) return
    const newQuantity = item.quantity + delta
    if (newQuantity <= 0) {
      removeFromCart(id)
    } else {
      updateItemMutation.mutate({ id, quantity: newQuantity })
    }
  }

  const removeFromCart = (id: number) => {
    removeItemMutation.mutate(id)
  }

  const toggleSelectItem = (id: number) => {
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
    )
  }

  const deleteSelectedItems = async () => {
    if (selectedItemIds.length === cart.length) {
      await clearCartMutation.mutateAsync()
    } else {
      for (const id of selectedItemIds) {
        await removeItemMutation.mutateAsync(id)
      }
    }
    setSelectedItemIds([])
  }

  const subtotal = parseFloat(String(cartData?.totals?.subtotal || '0').replace(/[^0-9.-]+/g, ""))
  const discountTotal = parseFloat(String(cartData?.totals?.discount_total || '0').replace(/[^0-9.-]+/g, ""))
  const tax = parseFloat(String(cartData?.totals?.tax_total || '0').replace(/[^0-9.-]+/g, ""))
  const deliveryCost = saleType === 'delivery' ? deliveryDetails.deliveryCost : 0
  const totalBeforePoints = parseFloat(String(cartData?.totals?.total || '0').replace(/[^0-9.-]+/g, "")) + deliveryCost

  // Calcular puntos adquiridos (10% del total sin costo de envío)
  const pointsEarned = Math.floor((subtotal + tax) * 0.1)

  // Calcular descuento por puntos (1 punto = $1)
  const pointsDiscount = usePoints
    ? Math.min(pointsToUse, totalBeforePoints)
    : 0
  const total = totalBeforePoints - pointsDiscount

  const totalForCart = (nextCart: PosCartData) => {
    const productTotal = parseFloat(
      String(nextCart?.totals?.total || '0').replace(/[^0-9.-]+/g, ''),
    )
    const beforePoints = productTotal + deliveryCost
    const discount = usePoints ? Math.min(pointsToUse, beforePoints) : 0

    return beforePoints - discount
  }

  const openPayment = async () => {
    if (validateCartMutation.isPending) return

    try {
      const [validatedCart] = await Promise.all([
        validateCartMutation.mutateAsync(),
        refetchContext(),
      ])
      const validatedTotal = totalForCart(validatedCart)

      if (Math.abs(validatedTotal - total) >= 0.01) {
        toast.info('El carrito fue actualizado con los precios vigentes.')
      }

      if (validatedCart.regulatory?.requires_prescription
        && !validatedCart.regulatory.is_ready_for_checkout) {
        setShowPrescriptionModal(true)
      } else {
        setShowPaymentModal(true)
      }
    } catch {
      // La mutacion sincroniza el carrito invalido y muestra el error.
    }
  }

  // Obtener puntos disponibles del cliente seleccionado
  // actualmente no esta en el backend
  const customerPoints = selectedCustomer
    ? /* customers.find((c: any) => c.id === selectedCustomer)?.loyaltyPoints || */ 0
    : 0

  const generateTicketNumber = () => {
    let prefix = ''

    if (selectedPaymentMethod === 'transfer' || selectedPaymentMethod === 'card') {
      prefix = `${selectedBank?.name || ''}-`
    } else if (selectedPaymentMethod === 'cash') {
      prefix = 'EF-'
    } else if (selectedPaymentMethod === 'credit') {
      prefix = 'CR-'
    } else {
      prefix = 'VT-'
    }

    return `${prefix}${Math.floor(Math.random() * 9000) + 1000}`
  }

  const proceedToPayment = async () => {
    if (validateCartMutation.isPending) return

    let validatedCart: PosCartData

    try {
      validatedCart = await validateCartMutation.mutateAsync()
    } catch {
      return
    }

    const validatedTotal = totalForCart(validatedCart)

    if (Math.abs(validatedTotal - total) >= 0.01) {
      toast.info('El total cambio. Revisa los datos del pago antes de continuar.')

      return
    }

    // Refresh before checking because another operation may have changed the
    // balance or an administrator may have edited the register limit.
    if (selectedPaymentMethod === 'cash') {
      const contextResult = await refetchContext({ throwOnError: true }).catch(() => null)
      const freshCapacity = contextResult?.data?.cash_capacity

      if (!freshCapacity) {
        toast.error('No fue posible verificar la capacidad actual de la caja. Intente nuevamente.')
        return
      }

      const remainingCapacity = Number(freshCapacity.remaining_amount)
      if (validatedTotal > remainingCapacity) {
        toast.error('La venta supera la capacidad disponible de efectivo. Realice un retiro o seleccione otro método de pago.')
        return
      }
    }

    if (requiresInvoice) {
      if (customerType === 'general') {
        setInvoiceData(prev => ({
          ...prev,
          rfc: 'XAXX010101000',
          name: 'PUBLICO EN GENERAL',
          cfdi_use: 'S01',
          fiscal_regime: '616',
          zip_code: facturamaProfile?.IssuedIn?.ZipCode || facturamaProfile?.TaxAddress?.ZipCode || prev.zip_code || '',
        }))
      } else {
        const c = customers.find((c: any) => c.id === selectedCustomer)
        if (c) {
          setInvoiceData(prev => ({
            ...prev,
            rfc: c.rfc || prev.rfc,
            name: c.name || prev.name,
            cfdi_use: c.cfdi_use || prev.cfdi_use,
            fiscal_regime: c.fiscal_regime || prev.fiscal_regime,
            zip_code: c.zip_code || prev.zip_code,
            email: c.email || prev.email,
          }))
        }
      }
      setShowInvoiceDataModal(true)
    } else {
      const ticket = generateTicketNumber()
      setTicketNumber(ticket)
      setShowPaymentModal(false)
      setShowTicketPreview(true)
    }
  }

  const confirmInvoiceData = () => {
    setShowInvoiceDataModal(false)
    const ticket = generateTicketNumber()
    setTicketNumber(ticket)
    setShowPaymentModal(false)
    setShowTicketPreview(true)
  }

  const resetSaleState = () => {
    setShowSuccessModal(false)
    setSelectedPaymentMethod(null)
    setCashReceived('')
    setSaleType('regular')
    setCustomerType('general')
    setSelectedCustomer(null)
    setSelectedDeliveryPerson('')
    setDeliveryNotes('')
    setRequiresInvoice(false)
    setTicketCopies(1)
    setSelectedBank(null)
    setReference('')
    setDeliveryDetails({
      deliveryUserId: null,
      recipientName: '',
      customerPhone: '',
      customerAddress: '',
      observations: '',
      deliveryCost: 0,
    })
    setUsePoints(false)
    setPointsToUse(0)
    setInvoiceStatus('idle')
    setInvoiceError(null)
    setCfdiPdfBase64(null)
    setInvoiceData({
      rfc: '', name: '', cfdi_use: '', fiscal_regime: '', zip_code: '', email: ''
    })
  }

  const completeSale = () => {
    const paymentMethod = paymentMethods.find(
      (method) => method.code === selectedPaymentMethod && method.is_active,
    )

    if (!paymentMethod) {
      toast.error('El método de pago seleccionado no está disponible.')

      return
    }

    const payload: any = {
      idempotency_key: generateUUID(),
      customer_id: selectedCustomer || null,
      requires_invoice: requiresInvoice,
      payment_method_id: paymentMethod.id,
    }

    if (selectedPaymentMethod === 'cash') {
      payload.cash_received_amount = cashReceived || total.toString()
    } else    if (selectedPaymentMethod === 'card' || selectedPaymentMethod === 'credit' || selectedPaymentMethod === 'transfer') {
      if (selectedBank?.name?.trim()) payload.bank_name = selectedBank.name.trim()
      if (selectedBank?.id) payload.bank_id = selectedBank.id
      if (reference.trim()) payload.reference = reference.trim()
    }

    if (requiresInvoice) {
      payload.invoice_receiver = invoiceData
    }

    if (saleType === 'delivery') {
      payload.delivery = {
        delivery_user_id: deliveryDetails.deliveryUserId,
        recipient_name: deliveryDetails.recipientName,
        recipient_phone: deliveryDetails.customerPhone,
        address: deliveryDetails.customerAddress,
        observations: deliveryDetails.observations
      }
    }

    if (requiresInvoice) {
      setInvoiceStatus('loading')
      setInvoiceError(null)
      setCfdiPdfBase64(null)
    }

    processSaleMutation.mutate(payload, {
      onSuccess: (data: any) => {
        setTicketNumber(data?.ticket_number || data?.folio || data?.id?.toString() || generateTicketNumber())
        setCompletedSaleTotal(total)

        if (requiresInvoice) {
          if (data?.invoice_error) {
            setInvoiceStatus('error')
            setInvoiceError(data.invoice_error)
          } else {
            setInvoiceStatus('success')
            if (data?.sale?.cfdi_id) {
              setCfdiPdfBase64(data.sale.cfdi_id)
            }
          }
        }

        setShowTicketPreview(false)
        setShowPaymentModal(false)
        setShowSuccessModal(true)
      }
    })
  }

  const handleDownloadPdf = async () => {
    if (!cfdiPdfBase64) return
    try {
      toast.info("Descargando PDF...")
      const base64 = await facturamaService.downloadInvoicePdf(cfdiPdfBase64)
      const linkSource = `data:application/pdf;base64,${base64}`
      const downloadLink = document.createElement("a")
      downloadLink.href = linkSource
      downloadLink.download = `Factura_${ticketNumber}.pdf`
      downloadLink.click()
      toast.success("PDF descargado correctamente")
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error al descargar el PDF")
    }
  }

  const handleDownloadXml = async () => {
    if (!cfdiPdfBase64) return
    try {
      toast.info("Descargando XML...")
      const base64 = await facturamaService.downloadInvoiceXml(cfdiPdfBase64)
      const linkSource = `data:application/xml;base64,${base64}`
      const downloadLink = document.createElement("a")
      downloadLink.href = linkSource
      downloadLink.download = `Factura_${ticketNumber}.xml`
      downloadLink.click()
      toast.success("XML descargado correctamente")
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Error al descargar el XML")
    }
  }

  const generateUUID = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID()
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0
      const v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }

  const handleCashWithdrawal = () => {
    const amount = parseFloat(withdrawalAmount)
    if (
      amount > 0 &&
      amount <= currentCashInRegister &&
      withdrawalConcept.trim()
    ) {
      withdrawalMutation.mutate({
        idempotency_key: generateUUID(),
        amount: amount.toString(),
        concept: withdrawalConcept.trim()
      })
    }
  }

  const handleCashDeposit = () => {
    const amount = parseFloat(depositAmount)
    const remainingCapacity = Number(registerContext?.cash_capacity?.remaining_amount ?? 0)

    if (registerContext?.cash_capacity && amount > remainingCapacity) {
      toast.error('El ingreso supera la capacidad disponible de la caja.')
      return
    }

    if (amount > 0 && depositConcept.trim()) {
      depositMutation.mutate({
        idempotency_key: generateUUID(),
        amount: amount.toString(),
        concept: depositConcept.trim()
      })
    }
  }

  const calculateDenominationsTotal = (denom: typeof denominations) => {
    return Object.entries(denom).reduce((total, [value, quantity]) => {
      return total + parseFloat(value) * quantity
    }, 0)
  }

  const handleDenominationChange = (denomination: number, quantity: number) => {
    const newDenominations = { ...denominations, [denomination]: quantity }
    setDenominations(newDenominations)
    const total = calculateDenominationsTotal(newDenominations)
    setCountedCash(total.toString())
  }

  const handleCashCount = () => {
    // Convert object to array for backend
    const denominationsArray = Object.entries(denominations)
      .filter(([_, quantity]) => quantity > 0)
      .map(([denom, quantity]) => ({
        denomination: parseInt(denom),
        quantity
      }))

    closeSessionMutation.mutate({
      denominations: denominationsArray,
      notes: cashCountNotes.trim() || 'Corte final'
    })
  }

  const closeCashCountResult = () => {
    setShowCashCountResult(false)
    setCountedCash('')
    setCashCountDifference(0)
    setCashCountNotes('')
  }

  const handleReleaseRegister = () => {
    if (!registerContext?.cash_register?.id) return
    releaseMutation.mutate(releasePassword)
  }

  const change =
    selectedPaymentMethod === 'cash'
      ? parseFloat(cashReceived || '0') - total
      : 0
  const cashCapacity = registerContext?.cash_capacity ?? registerContext?.session?.cash_capacity ?? null
  const cashWarning = cashCapacity?.status === 'WARNING'
  const cashCritical = cashCapacity?.status === 'CRITICAL'

  useHotkeys(['right', 'down', 'left', 'up'], (e) => {
    const grid = document.getElementById('pos-product-grid')
    if (grid && !grid.contains(document.activeElement)) {
      const firstProduct = grid.querySelector('[tabindex="0"]') as HTMLElement
      if (firstProduct) {
        e.preventDefault()
        firstProduct.focus()
      }
    }
  }, { enableOnFormTags: false })

  return (
    <ErrorBoundary>
      <div className="relative flex h-[calc(100vh-3.5rem)] flex-col lg:h-full lg:flex-row">
        {/* Left Side - Products & Context */}
        <div className="flex flex-1 flex-col overflow-hidden p-6">
          {/* Header with cashier info */}
          <div className="mb-4 flex items-center justify-between">
            <h1 className="text-2xl font-semibold text-slate-900">
              Punto de Venta
            </h1>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2">
                <Monitor className="h-4 w-4 text-slate-600" />
                <span className="text-sm font-medium text-slate-900">
                  {registerContext?.cash_register?.name || 'Caja No Asignada'}
                </span>
                {registerContext?.cash_register?.id && (
                  <button
                    onClick={() => setShowChangeCashier(true)}
                    className="ml-2 rounded p-1 hover:bg-slate-100"
                  >
                    <Key className="h-4 w-4 text-slate-600" />
                  </button>
                )}
              </div>

              {/* Cash in register */}
              <div
                className={`rounded-lg border px-4 py-2 ${cashCritical
                  ? 'border-red-200 bg-red-50'
                  : cashWarning
                    ? 'border-yellow-200 bg-yellow-50'
                    : 'border-slate-200 bg-white'
                  }`}
              >
                <div className="flex items-center gap-2">
                  {isLoadingContext ? (
                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                  ) : (
                    <>
                      <Banknote
                        className={`h-4 w-4 ${cashCritical
                          ? 'text-red-600'
                          : cashWarning
                            ? 'text-yellow-600'
                            : 'text-green-600'
                          }`}
                      />
                      <span className="text-sm font-semibold">
                        ${currentCashInRegister.toFixed(2)} MXN
                      </span>
                    </>
                  )}
                </div>
              </div>

              <button
                onClick={() => setShowWbsModal(true)}
                className="rounded-lg border border-slate-200 bg-white p-2 hover:bg-slate-50"
                title="Recargas y Servicios"
              >
                <Zap className="h-4 w-4 text-orange-500" />
              </button>

              <button
                onClick={() => setShowCashWithdrawal(true)}
                className="rounded-lg border border-slate-200 bg-white p-2 hover:bg-slate-50"
                title="Retiro de efectivo"
              >
                <LogOut className="h-4 w-4 text-red-600" />
              </button>

              <button
                onClick={() => setShowCashDeposit(true)}
                className="rounded-lg border border-slate-200 bg-white p-2 hover:bg-slate-50"
                title="Ingreso de efectivo"
              >
                <LogIn className="h-4 w-4 text-green-600" />
              </button>

              <button
                onClick={() => setShowCashCount(true)}
                className="rounded-lg bg-green-600 p-2 text-white hover:bg-green-700"
                title="Corte de caja"
              >
                <DollarSign className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Cash limit warning */}
          {(cashWarning || cashCritical) && cashCapacity && (
            <div className={`mb-4 flex items-center gap-2 rounded-lg border p-3 ${cashCritical ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'}`}>
              <AlertCircle className={`h-5 w-5 ${cashCritical ? 'text-red-600' : 'text-yellow-600'}`} />
              <p className={`text-sm ${cashCritical ? 'text-red-800' : 'text-yellow-800'}`}>
                {cashCapacity.label}: {cashCapacity.message}
                {' '}Disponible: ${Number(cashCapacity.remaining_amount).toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN.
              </p>
            </div>
          )}

          <ProductDiscoveryControls
            search={searchTerm}
            useCaseId={selectedUseCase}
            loading={isLoadingData}
            canLoadMore={productPage < lastProductPage}
            onSearchChange={(value) => { setSearchTerm(value); setProductPage(1) }}
            onUseCaseChange={(value) => { setSelectedUseCase(value); setProductPage(1) }}
            onLoadMore={() => setProductPage((current) => current + 1)}
          />

          {/* Products Grid */}
          <div className="flex-1 overflow-y-auto">
            <div 
              id="pos-product-grid"
              className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
              onKeyDown={(e) => {
                if (['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp'].includes(e.key)) {
                  const active = document.activeElement as HTMLElement;
                  if (!active || !e.currentTarget.contains(active)) return;
                  e.preventDefault();
                  
                  const items = Array.from(e.currentTarget.children) as HTMLElement[];
                  const index = items.indexOf(active);
                  if (index === -1) return;
                  
                  let cols = 1;
                  if (window.innerWidth >= 1280) cols = 3;
                  else if (window.innerWidth >= 768) cols = 2;
                  
                  let nextIndex = index;
                  if (e.key === 'ArrowRight') nextIndex = Math.min(index + 1, items.length - 1);
                  if (e.key === 'ArrowLeft') nextIndex = Math.max(index - 1, 0);
                  if (e.key === 'ArrowDown') nextIndex = Math.min(index + cols, items.length - 1);
                  if (e.key === 'ArrowUp') nextIndex = Math.max(index - cols, 0);
                  
                  items[nextIndex]?.focus();
                }
              }}
            >
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  addToCart={addToCart}
                />
              ))}
            </div>
            {!isLoadingData && products.length === 0 && (
              <p className="py-12 text-center text-sm text-slate-500">No se encontraron productos.</p>
            )}

            {/* Boton de cargar mas movido al fondo de la lista */}
            {productPage < lastProductPage && (
              <div className="py-6 text-center">
                <Button
                  variant="outline"
                  disabled={isLoadingData}
                  onClick={() => setProductPage((current) => current + 1)}
                  className="w-full max-w-xs"
                >
                  {isLoadingData ? 'Cargando...' : 'Cargar más productos'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Cart */}
        <PosCart
          saleType={saleType}
          setSaleType={setSaleType}
          deliveryDetails={deliveryDetails}
          setShowDeliveryDetailsModal={setShowDeliveryDetailsModal}
          customerType={customerType}
          setCustomerType={setCustomerType}
          selectedCustomer={selectedCustomer}
          setSelectedCustomer={setSelectedCustomer}
          setShowNewCustomerModal={setShowNewCustomerModal}
          customerPoints={customerPoints}
          pointsEarned={pointsEarned}
          usePoints={usePoints}
          setUsePoints={setUsePoints}
          pointsToUse={pointsToUse}
          setPointsToUse={setPointsToUse}
          totalBeforePoints={totalBeforePoints}
          cart={cart}
          clearCart={() => clearCartMutation.mutate()}
          deleteSelectedItems={deleteSelectedItems}
          toggleSelectItem={toggleSelectItem}
          removeFromCart={removeFromCart}
          updateQuantity={updateQuantity}
          subtotal={subtotal}
          tax={tax}
          deliveryCost={deliveryCost}
          pointsDiscount={pointsDiscount}
          total={total}
          onProcessPayment={openPayment}
          isValidatingCart={validateCartMutation.isPending}
          prescriptions={cartData?.prescriptions}
          regulatory={cartData?.regulatory}
          onEditPrescription={() => setShowPrescriptionModal(true)}
        />

        {/* Payment Modal */}
        <PaymentModal open={showPaymentModal}
          onOpenChange={setShowPaymentModal}
          total={total}
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          cashReceived={cashReceived}
          setCashReceived={setCashReceived}
          change={change}
          selectedBank={selectedBank}
          setSelectedBank={setSelectedBank}
          reference={reference}
          setReference={setReference}
          requiresInvoice={requiresInvoice}
          setRequiresInvoice={setRequiresInvoice}
          ticketCopies={ticketCopies}
          setTicketCopies={setTicketCopies}
          proceedToPayment={proceedToPayment}
          isValidatingCart={validateCartMutation.isPending}
          cashCapacity={cashCapacity} />

        {/* Ticket Preview Modal */}
        <TicketPreviewModal
          open={showTicketPreview}
          onOpenChange={setShowTicketPreview}
          ticketNumber={ticketNumber}
          branchName={registerContext?.sucursal?.name}
          cashRegisterName={registerContext?.cash_register?.name}
          cashierName={[registerContext?.session?.opened_by?.name, registerContext?.session?.opened_by?.last_name].filter(Boolean).join(' ') || 'Admin'}
          customerType={customerType}
          selectedCustomer={selectedCustomer}
          customers={customers}
          saleType={saleType}
          deliveryDetails={deliveryDetails}
          cart={cart}
          subtotal={subtotal}
          discountTotal={discountTotal}
          tax={tax}
          deliveryCost={deliveryCost}
          pointsDiscount={pointsDiscount}
          total={total}
          pointsEarned={pointsEarned}
          pointsToUse={pointsToUse}
          selectedPaymentMethod={selectedPaymentMethod}
          selectedBank={selectedBank?.name || ''}
          reference={reference}
          cashReceived={cashReceived}
          change={change}
          requiresInvoice={requiresInvoice}
          ticketCopies={ticketCopies}
          completeSale={completeSale}
          setShowPaymentModal={setShowPaymentModal}
        />

        {/* Success Modal */}
        <SuccessModal open={showSuccessModal}
          onOpenChange={(open) => {
            if (!open) resetSaleState()
          }}
          ticketNumber={ticketNumber}
          total={completedSaleTotal}
          requiresInvoice={requiresInvoice}
          invoiceStatus={invoiceStatus}
          invoiceError={invoiceError}
          cfdiPdfBase64={cfdiPdfBase64}
          onDownloadPdf={handleDownloadPdf}
          onDownloadXml={handleDownloadXml}
          onClose={resetSaleState}
        />

        {/* Cash Withdrawal Modal */}
        <CashWithdrawalModal
          open={showCashWithdrawal}
          onOpenChange={setShowCashWithdrawal}
          currentCashInRegister={currentCashInRegister}
          withdrawalAmount={withdrawalAmount}
          setWithdrawalAmount={setWithdrawalAmount}
          withdrawalConcept={withdrawalConcept}
          setWithdrawalConcept={setWithdrawalConcept}
          isSubmitting={withdrawalMutation.isPending}
          handleCashWithdrawal={handleCashWithdrawal}
          setShowCashWithdrawal={setShowCashWithdrawal}
        />

        {/* Cash Deposit Modal */}
        <CashDepositModal
          open={showCashDeposit}
          onOpenChange={setShowCashDeposit}
          currentCashInRegister={currentCashInRegister}
          depositAmount={depositAmount}
          setDepositAmount={setDepositAmount}
          depositConcept={depositConcept}
          setDepositConcept={setDepositConcept}
          isSubmitting={depositMutation.isPending}
          handleCashDeposit={handleCashDeposit}
          setShowCashDeposit={setShowCashDeposit}
          cashCapacity={cashCapacity}
        />

        {/* Cash Count Modal */}
        <CashCountModal
          open={showCashCount}
          onOpenChange={setShowCashCount}
          denominations={denominations}
          handleDenominationChange={handleDenominationChange}
          countedCash={countedCash}
          setCountedCash={setCountedCash}
          handleCashCount={handleCashCount}
          setShowCashCount={setShowCashCount}
          isSubmitting={closeSessionMutation.isPending}
          notes={cashCountNotes}
          setNotes={setCashCountNotes}
          currentCashInRegister={currentCashInRegister}
        />

        {/* Cash Count Result Modal */}
        <CashCountResultModal
          open={showCashCountResult}
          onOpenChange={setShowCashCountResult}
          cashCountStatus={cashCountStatus}
          cashierNumber={registerContext?.cash_register?.id || 1}
          cashierName={[registerContext?.session?.opened_by?.name, registerContext?.session?.opened_by?.last_name].filter(Boolean).join(' ') || 'Admin'}
          currentCashInRegister={currentCashInRegister}
          countedCash={countedCash}
          denominations={denominations}
          cashCountDifference={cashCountDifference}
          sessionData={closedSessionData}
          sucursalName={registerContext?.sucursal?.name}
          closeCashCountResult={() => setShowCashCountResult(false)}
        />

        {/* Change Cashier Modal */}
        <ChangeCashierModal
          open={showChangeCashier}
          onOpenChange={setShowChangeCashier}
          password={releasePassword}
          setPassword={setReleasePassword}
          handleReleaseRegister={handleReleaseRegister}
          setShowChangeCashier={setShowChangeCashier}
          isSubmitting={releaseMutation.isPending}
        />

        {/* Prescription Modal */}
        <PrescriptionModal open={showPrescriptionModal}
          onOpenChange={setShowPrescriptionModal}
          items={cart}
          prescriptions={cartData?.prescriptions || []}
          onSaved={() => {
            setShowPrescriptionModal(false)
            setShowPaymentModal(true)
          }} />

        {/* New Customer Modal - Quick Registration from POS */}
        <NewCustomerModal open={showNewCustomerModal}
          onOpenChange={setShowNewCustomerModal}
          setShowNewCustomerModal={setShowNewCustomerModal}
          onSuccess={reloadCustomers} />

        {/* Delivery Details Modal */}
        <DeliveryDetailsModal open={showDeliveryDetailsModal}
          onOpenChange={setShowDeliveryDetailsModal}
          deliveryDetails={deliveryDetails}
          setDeliveryDetails={setDeliveryDetails}

          setShowDeliveryDetailsModal={setShowDeliveryDetailsModal} />

        {/* Register Modals */}
        <RegisterAssignmentModal
          open={showRegisterAssignment}
          onSuccess={() => refetchContext()}
        />
        <OpenRegisterSessionModal
          open={showOpenSession}
          onOpenChange={setShowOpenSession}
          onReleaseRegister={() => {
            setShowOpenSession(false)
            setShowChangeCashier(true)
          }}
          onSuccess={() => refetchContext()}
          cashCapacity={cashCapacity}
        />

        <InvoiceDataModal
          open={showInvoiceDataModal}
          onOpenChange={setShowInvoiceDataModal}
          data={invoiceData}
          setData={setInvoiceData}
          onConfirm={confirmInvoiceData}
        />
        <PriceCheckModal
          isOpen={showPriceCheckModal}
          onClose={() => setShowPriceCheckModal(false)}
          onAddToCart={(product) => addToCart(product)}
        />
        
        {/* WBS Recharge Modal */}
        <WbsRechargeModal
          open={showWbsModal}
          onOpenChange={setShowWbsModal}
        />

        <div className="absolute bottom-4 left-4 z-50">
          <ShortcutsWidget
            expanded={showShortcutsHelp}
            setExpanded={setShowShortcutsHelp}
            onPriceCheck={() => setShowPriceCheckModal(true)}
          />
        </div>
      </div>
    </ErrorBoundary>
  )
}
